import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, CheckCircle, XCircle, Clock, RefreshCw, X as XIcon, Filter } from 'lucide-react';
import { apiListAccessRequests, apiApproveAccessRequest, apiRejectAccessRequest } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

// Helpers: /api/filters/sedes returns objects {value, label, code} — handle both formats safely
const sedeStr = (s) => (typeof s === 'object' ? (s?.value || '') : (s || ''));
const sedeCodeOf = (s) => sedeStr(s).split(' - ')[0].trim();
const sedeLabelOf = (s) => (typeof s === 'object' ? (s?.label || s?.value || '') : (s || ''));

function ApproveDialog({ open, onClose, request }) {
  const qc = useQueryClient();
  const safeSedes = Array.isArray(request?.requested_sedes) ? request.requested_sedes : [];
  const [selected, setSelected] = useState(
    safeSedes.map(s => String(s).split(' - ')[0].trim()).filter(Boolean)
  );
  const [sedeSearch, setSedeSearch] = useState('');
  const { data: sedesRaw = [] } = useQuery({
    queryKey: ['sedes-list'],
    queryFn: () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/filters/sedes`).then(r => r.json()),
    staleTime: 10 * 60 * 1000,
  });

  const filteredSedes = sedesRaw
    .filter(s => {
      const code = sedeCodeOf(s);
      const str = sedeStr(s);
      return str.toLowerCase().includes(sedeSearch.toLowerCase()) && !selected.includes(code);
    })
    .slice(0, 30);

  const approveMut = useMutation({
    mutationFn: () => apiApproveAccessRequest(request.id, selected, null),
    onSuccess: () => {
      toast.success(`Acceso aprobado para ${request.email}`);
      qc.invalidateQueries(['admin-access-requests']);
      onClose();
    },
    onError: (err) => { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Error al aprobar'); },
  });

  const rejectMut = useMutation({
    mutationFn: () => apiRejectAccessRequest(request.id, null, ''),
    onSuccess: () => {
      toast.success(`Solicitud rechazada para ${request.email}`);
      qc.invalidateQueries(['admin-access-requests']);
      onClose();
    },
    onError: (err) => { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Error al rechazar'); },
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', maxWidth: 500 }} data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ShieldCheck size={18} style={{ color: '#00C853' }} />
            Gestionar solicitud de acceso
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg p-3 text-sm" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--vz-border-subtle)' }}>
            <p className="font-semibold text-white">{request?.name}</p>
            <p className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{request?.email}</p>
            {request?.message && <p className="text-xs mt-1" style={{ color: 'var(--vz-text-muted)' }}>{request?.message}</p>}
            {Array.isArray(request?.requested_sedes) && request.requested_sedes.length > 0 && (
              <p className="text-xs mt-1" style={{ color: 'var(--vz-text-muted)' }}>
                Sedes solicitadas: {request.requested_sedes.map(s => typeof s === 'object' ? (s?.label || s?.value || '') : String(s)).join(', ')}
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs mb-2 block" style={{ color: 'var(--vz-text-secondary)' }}>Sedes a asignar ({selected.length})</Label>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2 p-2 rounded-lg" style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.15)' }}>
                {selected.map(s => (
                  <button key={s} onClick={() => setSelected(prev => prev.filter(x => x !== s))}
                    className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded font-mono"
                    style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}
                  >
                    {s} <XIcon size={9} />
                  </button>
                ))}
              </div>
            )}
            <Input
              value={sedeSearch}
              onChange={e => setSedeSearch(e.target.value)}
              placeholder="Buscar sede..."
              className="text-xs h-8 mb-2"
              style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'white' }}
            />
            <div className="grid grid-cols-3 gap-1 max-h-36 overflow-y-auto">
              {filteredSedes.map(s => (
                <button key={sedeStr(s)} onClick={() => setSelected(prev => [...prev, sedeCodeOf(s)])}
                  className="text-xs px-1.5 py-1 rounded text-left truncate"
                  style={{ background: 'rgba(255,255,255,0.04)', color: '#aaa', border: '1px solid rgba(255,255,255,0.07)' }}
                  title={sedeStr(s)}
                >
                  {sedeLabelOf(s)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => approveMut.mutate()}
              disabled={approveMut.isPending || rejectMut.isPending || selected.length === 0}
              className="flex-1 gap-2"
              style={{ background: '#00C853', color: '#000' }}
              data-testid="approve-dialog-confirm"
            >
              <CheckCircle size={14} />
              {approveMut.isPending ? 'Aprobando...' : `Aprobar (${selected.length})`}
            </Button>
            <Button
              onClick={() => rejectMut.mutate()}
              disabled={approveMut.isPending || rejectMut.isPending}
              variant="outline"
              className="flex-1 gap-2"
              style={{ borderColor: '#e53935', color: '#e53935', background: 'transparent' }}
              data-testid="reject-dialog-btn"
            >
              <XCircle size={14} />
              {rejectMut.isPending ? 'Rechazando...' : 'Rechazar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_FILTERS = [
  { key: 'pending',  label: 'Pendientes',  color: '#FFB900' },
  { key: 'approved', label: 'Aprobadas',   color: '#00C853' },
  { key: 'rejected', label: 'Rechazadas',  color: '#e53935' },
  { key: 'all',      label: 'Todas',       color: '#aaa' },
];

export function SolicitudesTab() {
  const [approveRequest, setApproveRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const { data: requests = [], isLoading, refetch } = useQuery({
    queryKey: ['admin-access-requests'],
    queryFn: () => apiListAccessRequests(),
    staleTime: 30 * 1000,
  });

  const pending = requests.filter(r => r.status === 'pending');
  const approved = requests.filter(r => r.status === 'approved');
  const rejected = requests.filter(r => r.status === 'rejected');

  const displayed = statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter);

  return (
    <div className="space-y-4" data-testid="solicitudes-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold text-white">Solicitudes de Acceso</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
            {pending.length} pendiente{pending.length !== 1 ? 's' : ''} · {approved.length} aprobada{approved.length !== 1 ? 's' : ''} · {rejected.length} rechazada{rejected.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Filter size={11} style={{ color: 'var(--vz-text-muted)' }} />
          {STATUS_FILTERS.map(f => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className="text-xs px-2.5 py-1 rounded-full transition-all"
              data-testid={`filter-btn-${f.key}`}
              style={{
                background: statusFilter === f.key ? f.color + '22' : 'rgba(255,255,255,0.04)',
                color: statusFilter === f.key ? f.color : 'var(--vz-text-secondary)',
                border: `1px solid ${statusFilter === f.key ? f.color + '55' : 'rgba(255,255,255,0.08)'}`,
                fontWeight: statusFilter === f.key ? 600 : 400,
              }}
            >
              {f.label}
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => refetch()} className="text-xs gap-1.5 h-7 ml-1" style={{ color: 'var(--vz-text-secondary)' }}>
            <RefreshCw size={11} />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" style={{ background: 'var(--vz-bg-elevated)' }} />)}
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--vz-text-muted)' }}>
          <ShieldCheck size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay solicitudes {statusFilter !== 'all' ? `"${STATUS_FILTERS.find(f=>f.key===statusFilter)?.label?.toLowerCase()}"` : ''}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayed.map(req => (
            <div key={req.id}
              className="flex items-start justify-between p-4 rounded-xl"
              style={{
                background: 'var(--vz-bg-elevated)',
                border: `1px solid ${req.status === 'approved' ? 'rgba(0,200,83,0.2)' : req.status === 'rejected' ? 'rgba(229,57,53,0.2)' : 'rgba(255,185,0,0.2)'}`,
              }}
              data-testid={`access-request-row-${req.id}`}
            >
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-white truncate">{req.name}</p>
                  <Badge className="text-[10px] flex-shrink-0"
                    style={{
                      background: req.status === 'approved' ? 'rgba(0,200,83,0.1)' : req.status === 'rejected' ? 'rgba(229,57,53,0.1)' : 'rgba(255,185,0,0.1)',
                      color: req.status === 'approved' ? '#00C853' : req.status === 'rejected' ? '#e53935' : '#FFB900',
                      border: `1px solid ${req.status === 'approved' ? 'rgba(0,200,83,0.3)' : req.status === 'rejected' ? 'rgba(229,57,53,0.3)' : 'rgba(255,185,0,0.3)'}`,
                    }}
                  >
                    {req.status === 'approved' ? 'Aprobada' : req.status === 'rejected' ? 'Rechazada' : 'Pendiente'}
                  </Badge>
                </div>
                <p className="text-xs truncate" style={{ color: 'var(--vz-text-secondary)' }}>{req.email}</p>
                {req.message && <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--vz-text-muted)' }}>{req.message}</p>}
                <p className="text-[10px] mt-1 flex items-center gap-1" style={{ color: 'var(--vz-text-muted)' }}>
                  <Clock size={9} />
                  {new Date(req.created_at).toLocaleString('es-ES')}
                  {req.status === 'approved' && req.assigned_sedes?.length > 0 && (
                    <span style={{ color: '#00C853' }}> · {req.assigned_sedes.length} sedes asignadas</span>
                  )}
                </p>
              </div>
              {req.status === 'pending' && (
                <Button size="sm" className="flex-shrink-0 gap-1.5 text-xs h-8"
                  style={{ background: '#00C853', color: '#000' }}
                  onClick={() => setApproveRequest(req)}
                  data-testid={`approve-btn-${req.id}`}
                >
                  <CheckCircle size={13} /> Aprobar
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {approveRequest && (
        <ApproveDialog open={!!approveRequest} onClose={() => setApproveRequest(null)} request={approveRequest} />
      )}
    </div>
  );
}
